import { TAUX_CNSS_VENTILATION_PATRONALE } from '@shared/constants/Taux';
import { montantCotisation } from '@shared/utils/round';

/**
 * Ventilation de la CNSS patronale (décision du dirigeant du 23/10/2026 ; communiqué CNSS du 12 janvier 2026 portant
 * application du décret n° 0487/PR/MASI du 18 décembre 2025, en vigueur depuis le 1er janvier 2026) :
 * prestations familiales et maternité 5 % + accidents du travail et maladies professionnelles 2 % +
 * assurance vieillesse, invalidité et décès 11 % = 18 %. Même assiette (plafond 1 500 000 FCFA par mois) pour chaque composante.
 *
 * Invariant : `pf + atmp + vid === total`, et `total` est le montant de la CNSS patronale calculé sur le taux global
 * (18 %), arrondi une seule fois. Seule la RÉPARTITION est exposée : net à payer et coût employeur ne changent pas.
 * L'écart d'arrondi éventuel (au plus quelques FCFA) est porté par la composante VID.
 */
export interface VentilationCnssPatronale {
  /** Prestations familiales et maternité (5 %). */
  pf: number;
  /** Accidents du travail et maladies professionnelles (2 %). */
  atmp: number;
  /** Assurance vieillesse, invalidité et décès, part employeur (11 %). */
  vid: number;
  /** CNSS patronale totale (18 %). */
  total: number;
}

export interface TauxVentilationCnss {
  pf: number;
  atmp: number;
  vid: number;
}

export const TAUX_VENTILATION_CNSS_DEFAUT: TauxVentilationCnss = {
  pf: TAUX_CNSS_VENTILATION_PATRONALE.PF,
  atmp: TAUX_CNSS_VENTILATION_PATRONALE.ATMP,
  vid: TAUX_CNSS_VENTILATION_PATRONALE.VID,
};

/** Répartit la CNSS patronale sur la base plafonnée `baseCnss` (déjà plafonnée à 1 500 000 par l'appelant). */
export function ventilerCnssPatronale(
  baseCnss: number,
  taux: TauxVentilationCnss = TAUX_VENTILATION_CNSS_DEFAUT,
): VentilationCnssPatronale {
  const total = montantCotisation(baseCnss, taux.pf + taux.atmp + taux.vid);
  const pf = montantCotisation(baseCnss, taux.pf);
  const atmp = montantCotisation(baseCnss, taux.atmp);
  return { pf, atmp, vid: total - pf - atmp, total };
}

/**
 * Ventilation lue sur un bulletin. Un bulletin généré avant cette décision porte toute la CNSS patronale sur
 * `cnss_pf_emp` (`cnss_rp_emp` et `cnss_vid_emp` à 0) : sa répartition est alors reconstituée pour l'AFFICHAGE à partir de
 * son total, sans jamais modifier ses montants.
 */
export function ventilationCnssDuBulletin(b: {
  base_cnss?: number;
  cnss_pf_emp?: number;
  cnss_rp_emp?: number;
  cnss_vid_emp?: number;
}): VentilationCnssPatronale {
  const pf = b.cnss_pf_emp ?? 0;
  const atmp = b.cnss_rp_emp ?? 0;
  const vid = b.cnss_vid_emp ?? 0;
  const total = pf + atmp + vid;
  const base = b.base_cnss ?? 0;
  if (atmp === 0 && vid === 0 && pf > 0 && base > 0) {
    const pfReconstitue = montantCotisation(base, TAUX_VENTILATION_CNSS_DEFAUT.pf);
    const atmpReconstitue = montantCotisation(base, TAUX_VENTILATION_CNSS_DEFAUT.atmp);
    return {
      pf: pfReconstitue,
      atmp: atmpReconstitue,
      vid: Math.max(0, total - pfReconstitue - atmpReconstitue),
      total,
    };
  }
  return { pf, atmp, vid, total };
}
